import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { filter } from 'lodash';

// Components
import Card from 'components/Layout/Card';
import Loader from 'components/Layout/Loader';
import Button from 'components/form/Button';
import Modal from 'components/Layout/Modal';
import CustomCertificate from 'components/Layout/CustomCertificate';
import DeletePopup from 'components/Layout/DeletePopup';
import GenerateCertificateRequestPopup from './GenerateCertificateRequestPopup';
import ActionFormPopup from './ActionFormPopup';
import RequestVerificationModal from 'pages/Common/RequestRaisedAndReceived/Common/RequestVerificationModal';
import VerifyOtpModel from 'pages/Common/RequestRaisedAndReceived/Common/VerifyOtpModel';

// Constants and Helpers
import { certificatePath, icons } from 'utils/constants';
import {
  generatePreSignedUrl,
  objectToFormData,
  getFormDataArrayObject,
  getStatus,
  getDataFromLocalStorage,
} from 'utils/helpers';

// Redux Actions
import {
  fetchAllCertificateEventRequests,
  showSuccess,
  storeEventData,
  uploadEventCertificate,
  throwError,
  handleSendCertificateRequestOtp,
  handleVerifyCertificateOtpRequest,
  updateEventCertificateRequest,
} from 'store/slices';

// Styles
import './CertificateRequest.scss';
import usePermission from 'hooks/usePermission';

const CertificateRequestDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventId } = useParams();
  const userType = getDataFromLocalStorage('user_type');
  const { viewAccess } = usePermission("Requests");
  const [htmlContentData, setHtmlContentData] = useState('');
  const [certificateImg, setCertificateImg] = useState('');
  const [eventRequestData, setEventRequestData] = useState([]);
  const [eventCertificateData, setEventCertificateData] = useState([]);
  const [viewSample, setViewSample] = useState('');
  const [isRequestId, setIsRequestId] = useState('');
  const [edit, setEdit] = useState(false);
  const [isFormLoading, setIsFormLoading] = useState(true);
  const [otpVerification, setOtpVerification] = useState(false);
  const [isNewCertifcatePopup, setIsNewCertifcatePopup] = useState({
    show: false,
  });
  const [uploadData, setUploadData] = useState({ show: false, title: '' });
  const [openActionForm, setOpenActionForm] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isOpenConfirmationDialog, setIsOpenConfirmationDialog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showOtpDialog, setShowOtpDialog] = useState(false);
  const [otpData, setOtpData] = useState(false);

  const { comitteeMemberCategoryList } = useSelector((state) => ({
    comitteeMemberCategoryList: state.global.comitteeMemberCategoryList,
  }));

  const { show, title, sub_type, user_type, oldData } = uploadData;
  const initialValues = { comment: '', document: '' };

  const { event_details, ocm_category_ids, certificate_data } =
    eventCertificateData || {};

  const fetchEventDetails = async () => {
    const response = await dispatch(
      fetchAllCertificateEventRequests({ event_id: eventId })
    );
    if (response.status === 200) {
      setEventCertificateData(response.data);
    }
    setIsFormLoading(false);
  };

  const handleView = async (data) => {
    const certificateKey = data;
    if (!certificateKey) {
      dispatch(throwError({ message: 'Missing certificate file key.' }));
      return;
    }

    const s3File = await generatePreSignedUrl(certificateKey, certificatePath);
    setViewSample(`${s3File}`);
  };

  const handleEditTemplateData = (data) => {
    setEdit(data?.request_status === '0' ? true : false);
    setHtmlContentData(data?.certificate_html);
    setCertificateImg(data?.background_file);
    setEventRequestData(data?.request_details);
    setIsRequestId(data?.request_id);
  };

  const sendOtpRequest = async () => {
    setIsLoading(true);
    const payload = objectToFormData({ request_id: isRequestId });

    try {
      const response = await dispatch(handleSendCertificateRequestOtp(payload));
      setShowOtpDialog(true);
      if (response.status === 200) dispatch(showSuccess(response.message));
    } catch (error) {
      dispatch(throwError({ message: 'Failed to process request' }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (otp) => {
    setOtpVerification(true);
    const payload = getFormDataArrayObject({
      request_id: isRequestId,
      ...(otp && { otp }),
      document: otpData?.document,
      comment: otpData?.comment,
      request_status: otpData?.action,
    });
    try {
      const response = await dispatch(
        handleVerifyCertificateOtpRequest(payload)
      );
      if (response.status === 200) {
        setShowOtpDialog(false);
        setIsOpenConfirmationDialog(false);
        dispatch(showSuccess(response.message));
        fetchEventDetails();
      }
    } finally {
      setOtpVerification(false);
    }
  };

  const handleReject = async (values, setErrors) => {
    setIsRejecting(true);
    if (!values.comment.trim() && !values.document) {
      setErrors({
        comment: 'Either a comment or a document is required',
        document: 'Either a comment or a document is required',
      });
      return;
    }
    setOtpData({ ...values, action: '2' });
    await sendOtpRequest();
    setOpenActionForm(false);
  };
  const handleApproveRequest = async (values) => {
    setOtpData({ ...values, action: '1' });
    // if (userType === '1') {
    //   await sendOtpRequest();
    // } else {
    //   setIsOpenConfirmationDialog(true);
    // }
    setIsOpenConfirmationDialog(true)
    setOpenActionForm(false);
  };

  useEffect(() => {
    fetchEventDetails();
    return () => dispatch(storeEventData({}));
  }, []);

  let ocmList = filter(comitteeMemberCategoryList, (o) =>
    ocm_category_ids?.includes(o.id)
  );

  const certificateTypeData = [
    { id: '', user_type: '3', name: 'Keynote Speakers' },
    { id: '', user_type: '5', name: 'Session Speakers' },
    { id: '', user_type: '1', name: 'Participation' },
    {
      id: '',
      user_type: '4',
      name: 'Research Paper Presentation',
    },
  ];

  return (
    <>
      {isNewCertifcatePopup?.show && (
        <GenerateCertificateRequestPopup
          onHide={() => setIsNewCertifcatePopup({ show: false })}
          handleAPI={async (value, htmlContent, bgImg) => {
            const payload = {
              id: isRequestId,
              event_id: eventId,
              user_type: isNewCertifcatePopup?.user_type,
              user_type_category: isNewCertifcatePopup?.user_type_category,
              certificate_html: value,
              dummy_certificate_html: htmlContent,
              background_file: bgImg,
              type: edit ? 2 : 1,
            };
            return await dispatch(
              updateEventCertificateRequest(objectToFormData(payload))
            );
          }}
          handleSuccess={() => {
            fetchEventDetails();
            dispatch(showSuccess('Certificate upload successfully.'));
            // setIsNewCertifcatePopup({ show: false });
          }}
          setHtmlContentData={setHtmlContentData}
          htmlContentData={htmlContentData}
          certificateImg={certificateImg}
          handleAction={() => {
            setOpenActionForm(true);
            setIsNewCertifcatePopup({ show: false });
          }}
          edit={edit}
        />
      )}

      {isOpenConfirmationDialog && (
        <RequestVerificationModal
          handleConfirmation={() => handleVerifyOtp()}
          onHide={() => setIsOpenConfirmationDialog(false)}
          title={userType === '1' ? "Once approved, this request will be finalized. Do you want to continue?" : "You want to share the approve request to super admin"}
          isLoading={otpVerification}
        />
      )}

      {showOtpDialog && (
        <VerifyOtpModel
          title="Certificate Request"
          showApproveDialog={showOtpDialog}
          onHide={() => setShowOtpDialog(false)}
          onVerify={handleVerifyOtp}
          sendOtpRequest={sendOtpRequest}
          otpVerification={otpVerification}
        />
      )}

      {openActionForm && (
        <ActionFormPopup
          showApproveDialog={openActionForm}
          onHide={() => setOpenActionForm(false)}
          setIsRejecting={setIsRejecting}
          isRejecting={isRejecting}
          handleApproveRequest={handleApproveRequest}
          handleReject={handleReject}
          initialValues={initialValues}
          handleView={handleView}
          data={{ ...eventRequestData, ...event_details }}
          isLoading={isLoading}
        />
      )}

      {viewSample && (
        <Modal fullscreen largeClose onHide={() => setViewSample('')}>
          <iframe
            frameBorder="0"
            src={`${viewSample}#toolbar=0&navpanes=0`}
            title="Certificate Preview"
            style={{ width: '100%', height: '99%' }}
          />
        </Modal>
      )}

      {show && (
        <CustomCertificate
          title={title}
          oldData={oldData}
          handleAPI={async (values) => {
            const payload = {
              certificate: values.certificate,
              field_data: JSON.stringify(values.fieldData),
              event_id: eventId,
              user_type: user_type,
              user_type_category: sub_type,
            };
            return await dispatch(
              uploadEventCertificate(objectToFormData(payload))
            );
          }}
          handleSuccess={() => {
            fetchEventDetails();
            dispatch(showSuccess('Certificate upload successfully.'));
            setUploadData({ show: false, title: '' });
          }}
          onHide={() => setUploadData({ show: false, title: '' })}
        />
      )}

      {isFormLoading ? (
        <Card className="cmt-24 cmb-24 cps-30 cpe-30 cpt-40 cpb-40 d-flex align-items-center justify-content-center unset-br">
          <Loader size="md" />
        </Card>
      ) : (
        <>
          <Card className="d-flex gap-3 p-3 mb-3">
            <img
              src={icons.leftArrow}
              alt="back"
              className="h-21 pointer"
              onClick={() => navigate(-1)}
            />
            <div className="text-16-500">{event_details?.event_name}</div>
          </Card>

          {certificate_data?.map((elm, index) => {
            const {
              user_type,
              user_type_category,
              request_id,
              request_status,
            } = elm;
            let committeeName = '';

            if (user_type === '2') {
              const match = filter(
                comitteeMemberCategoryList,
                (o) => o.id === Number(user_type_category)
              );
              committeeName = match[0]?.name || '';
            } else {
              const match = certificateTypeData.find(
                (o) => o.user_type === user_type
              );
              committeeName = match?.name || '';
            }
            return (
              <Card
                key={index}
                className="cmb-20 p-3 unset-br d-flex justify-content-between align-items-center"
              >
                <div className="text-15-500 d-flex align-items-center">
                  {committeeName}
                  <div
                    className={`cms-10 certificate-status certificate-status-${getStatus(
                      request_status,
                      'request'
                    ).toLowerCase()}`}
                  >
                    {getStatus(request_status, 'request')}
                  </div>
                </div>

                {viewAccess === "1" &&
                  <Button
                    btnStyle="primary-outline"
                    className="h-35 text-14-400 gap-2"
                    text="View Request"
                    onClick={() => {
                      setIsNewCertifcatePopup({
                        show: true,
                        sub_type: request_id,
                        user_type: user_type || '2',
                        user_type_category: user_type_category || '',
                      });
                      handleEditTemplateData(elm);
                    }}
                    icon={<i className="bi bi-eye" />}
                  />}
              </Card>
            );
          })}
        </>
      )}
    </>
  );
};

export default CertificateRequestDetails;
